
import TutorPasswordBanner from "@/components/staff/TutorPasswordBanner.vue";
import store from "@/store";
import MIcon from "@/components/MIcon.vue";
import MNavBar from "@/components/MNavBar.vue";
import MButton from "@/components/MButton.vue";
import MHeaderPanel from "@/components/MHeaderPanel.vue";
import ServiceTopicAddModal from "@/components/topic/ServiceTopicAddModal.vue";
import ServiceTopicEditModal from "@/components/topic/ServiceTopicEditModal.vue";
import { Options, Vue } from "vue-class-component";
import { State, Getter } from "@/store/helper";
import { ServiceTopic } from "@/entities/service_topic";
import dayjs from "dayjs";
import firebase from "firebase";
import { School, SchoolConfig } from "../entities/school";
import {
  getServiceTopicsOfSchool,
  getServiceTopicsOfParentSchools
} from "@/api/service_topic";

type ServiceTopicDataToDisplay = {
  ref: firebase.firestore.DocumentReference;
  url: string;
  label: string;
  timestamp: number;
  date: string;
  isNew: boolean;
  isPublished: boolean;
  editable?: boolean;
};

@Options({
  components: {
    TutorPasswordBanner,
    MIcon,
    MNavBar,
    MButton,
    MHeaderPanel,
    ServiceTopicAddModal,
    ServiceTopicEditModal
  }
})
export default class Home extends Vue {
  @State("serviceTopics", "serviceTopic") serviceTopics!: ServiceTopic[];
  @State("serviceTopicsForSchoolAI", "serviceTopic")
  serviceTopicsForSchoolAI!: ServiceTopic[];
  @State("serviceTopicsOfSchool", "serviceTopic")
  serviceTopicsOfSchool!: ServiceTopic[];
  @State("serviceTopicsOfParentSchools", "serviceTopic")
  serviceTopicsOfParentSchools!: ServiceTopic[];
  @State("school") school!: School | null;
  @State("schoolConfig") schoolConfig!: SchoolConfig | null;
  @State("initialized", "serviceTopic") isServiceTopicsLoaded!: boolean;
  @Getter("isAdmin") isAdmin!: boolean;
  @Getter("isAdminOfSchool") isAdminOfSchool!: boolean;
  @Getter("isServiceProvider") isServiceProvider!: boolean;
  @Getter("isExclusiveSchoolAiUser") isExclusiveSchoolAiUser!: boolean;
  addModalVisible = false;
  updateModalVisible = false;
  addModalForSchoolAIVisible = false;
  updateModalForSchoolAIVisible = false;
  selectedServiceTopic: ServiceTopic | null = null;
  selectedServiceTopicForSchoolAI: ServiceTopic | null = null;
  serviceTopicsOfSchoolFetched = false;

  timerId = -1;

  clearInterval() {
    window.clearInterval(this.timerId);
  }

  async schoolLoadedWatcher(school: School | null) {
    if (!school) return;
    if (this.serviceTopicsOfSchool.length > 0) {
      this.serviceTopicsOfSchoolFetched = true;
      this.clearInterval();
      return;
    }

    try {
      const [serviceTopicsOfSchool, serviceTopicsOfParentSchools] =
        await Promise.all([
          getServiceTopicsOfSchool(),
          getServiceTopicsOfParentSchools(
            this.schoolConfig?.data.parentSchoolDocIds ?? []
          )
        ]);
      store.commit(
        "serviceTopic/SET_SERVICE_TOPICS_OF_SCHOOL",
        serviceTopicsOfSchool
      );
      store.commit(
        "serviceTopic/SET_SERVICE_TOPICS_OF_PARENT_SCHOOLS",
        serviceTopicsOfParentSchools
      );
      this.serviceTopicsOfSchoolFetched = true;
    } catch (e) {
      console.error("error when fetching service topics of school", e);
    } finally {
      this.clearInterval(); //エラーに関わらずタイマーは解除する
    }
  }

  get schoolName() {
    return this.school?.data.name;
  }

  private mapTopicsToDisplay(
    topics: ServiceTopic[],
    canSeeUnpublished: boolean
  ): ServiceTopicDataToDisplay[] {
    return topics
      .map(({ ref, data }) => {
        const d = dayjs.unix(data.timestamp).startOf("day");
        const isPublished = !d.isAfter(dayjs());
        return {
          ...data,
          ref,
          date: d.format("YYYY/MM/DD"),
          isPublished,
          isNew: isPublished && d.add(7, "day").isAfter(dayjs().startOf("day"))
        };
      })
      .filter(t => t.isPublished || canSeeUnpublished);
  }

  get serviceTopicsToDisplay(): ServiceTopicDataToDisplay[] {
    return this.mapTopicsToDisplay(this.serviceTopics, this.isAdmin);
  }

  get serviceTopicsToDisplayForSchoolAI(): ServiceTopicDataToDisplay[] {
    if (this.schoolConfig && !this.schoolConfig.data.useSchoolAiExclusively) {
      return [];
    }
    return this.mapTopicsToDisplay(this.serviceTopicsForSchoolAI, this.isAdmin);
  }

  get serviceTopicsToDisplayOfSchool(): ServiceTopicDataToDisplay[] {
    const own = this.mapTopicsToDisplay(
      this.serviceTopicsOfSchool,
      this.isAdminOfSchool
    ).map(topic => ({ ...topic, editable: this.isAdminOfSchool }));
    const parents = this.mapTopicsToDisplay(
      this.serviceTopicsOfParentSchools,
      false
    ).map(topic => ({ ...topic, editable: false }));
    return [...own, ...parents].sort((a, b) => b.timestamp - a.timestamp);
  }

  startUpdatingServiceTopic(serviceTopic: ServiceTopicDataToDisplay) {
    let serviceTopics: ServiceTopic[];
    if (this.isAdmin) {
      serviceTopics = this.serviceTopics;
    } else if (this.isAdminOfSchool) {
      serviceTopics = this.serviceTopicsOfSchool;
    } else {
      serviceTopics = [];
    }
    this.selectedServiceTopic = serviceTopics.find(
      _ => _.ref.id === serviceTopic.ref.id
    )!;
    this.updateModalVisible = true;
  }

  startUpdatingServiceTopicForSchoolAi(
    serviceTopic: ServiceTopicDataToDisplay
  ) {
    this.selectedServiceTopicForSchoolAI = this.serviceTopicsForSchoolAI.find(
      _ => _.ref.id === serviceTopic.ref.id
    )!;
    this.updateModalForSchoolAIVisible = true;
  }

  async mounted() {
    await store.dispatch("serviceTopic/setup");

    if (this.school) {
      if (this.timerId > 0) {
        this.clearInterval();
      }

      this.timerId = window.setInterval(() => {
        this.schoolLoadedWatcher(this.school);
      }, 500);
    }
  }

  beforeUnmount(): void {
    this.clearInterval();
  }
}
